import React, { useState, useEffect } from "react";
import { makeStyles } from "@mui/styles";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Button from "components/CustomButtons/Button.js";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import styles from '../../styles/headerLinksModernStyle.js';
import { useSelector } from "react-redux";
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import LanguageMenu from '../../components/LanguageMenu.jsx';
import { Grow, Box } from '@mui/material';

const useStyles = makeStyles(styles);

/**
 * HeaderLinksModern - Uber-style navigation with dropdowns
 */
export default function HeaderLinksModern(props) {
  const classes = useStyles();
  const auth = useSelector(state => state.auth);
  const { t } = useTranslation();
  const [loggedIn, setLoggedIn] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const homePath = props.homePath || '/';
  const bookingPath = props.bookingPath || '/addbookings';
  const navigation = props.navigation || {};

  // Dropdown state
  const [rideAnchor, setRideAnchor] = useState(null);
  const [driveAnchor, setDriveAnchor] = useState(null);
  const [aboutAnchor, setAboutAnchor] = useState(null);

  const isActive = (path) => location.pathname === path;

  useEffect(() => {
    if (auth.profile && auth.profile.uid) {
      setLoggedIn(true);
    } else {
      setLoggedIn(false);
    }
  }, [auth.profile]);

  const handleRideClick = (event) => setRideAnchor(event.currentTarget);
  const handleRideClose = () => setRideAnchor(null);
  const handleDriveClick = (event) => setDriveAnchor(event.currentTarget);
  const handleDriveClose = () => setDriveAnchor(null);
  const handleAboutClick = (event) => setAboutAnchor(event.currentTarget);
  const handleAboutClose = () => setAboutAnchor(null);

  const closeAll = () => {
    setRideAnchor(null);
    setDriveAnchor(null);
    setAboutAnchor(null);
  };

  const navigateTo = (path, state) => {
    navigate(path, state ? { state } : undefined);
    closeAll();
  };

  const menuPaperProps = {
    elevation: 3,
    sx: {
      mt: 1,
      borderRadius: 3,
      minWidth: 200,
      boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
      border: '1px solid rgba(0,0,0,0.08)',
    },
  };

  const menuItemSx = {
    py: 1.5,
    px: 2.5,
    fontSize: 15,
    '&:hover': {
      backgroundColor: 'rgba(0,0,0,0.04)',
    },
  };

  const defaultRideLinks = [
    { label: 'home', path: homePath },
    { label: 'book_a_ride', path: bookingPath, requiresAuth: true },
    { label: 'shop', path: '/shop' },
  ];
  const defaultDriveLinks = [
    { label: 'become_a_driver', path: '/login' },
    { label: 'become_partner', path: '/contact-us' },
  ];
  const defaultAboutLinks = [
    { label: 'about_us', path: '/about-us' },
    { label: 'contact_us', path: '/contact-us' },
    { label: 'privacy_policy', path: '/privacy-policy' },
  ];

  const rideLinks = (navigation.rideLinks || defaultRideLinks).filter((item) => item.visible !== false);
  const driveLinks = (navigation.driveLinks || defaultDriveLinks).filter((item) => item.visible !== false);
  const aboutLinks = (navigation.aboutLinks || defaultAboutLinks).filter((item) => item.visible !== false);

  const goToLink = (item) => {
    if (item.requiresAuth && !loggedIn) {
      navigateTo('/login', { from: item.path });
      return;
    }
    navigateTo(item.path, item.state);
  };

  return (
    <List className={classes.list}>
      {/* Ride Menu */}
      <ListItem className={classes.listItem}>
        <Button
          aria-haspopup="true"
          aria-expanded={Boolean(rideAnchor) ? 'true' : 'false'}
          color="transparent"
          className={
            isActive(homePath) || isActive(bookingPath)
              ? classes.navLink + ' ' + classes.navLinkActive
              : classes.navLink
          }
          onClick={handleRideClick}
          endIcon={<KeyboardArrowDownIcon sx={{ ml: -0.5, fontSize: 20 }} />}
        >
          {t('ride') || 'Ride'}
        </Button>
        <Menu
          id="ride-menu"
          anchorEl={rideAnchor}
          open={Boolean(rideAnchor)}
          onClose={handleRideClose}
          TransitionComponent={Grow}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={menuPaperProps}
        >
          {rideLinks.map((item, idx) => (
            <MenuItem key={`ride-${idx}`} onClick={() => goToLink(item)} sx={menuItemSx}>
              {t(item.label) || item.label}
            </MenuItem>
          ))}
          {loggedIn && !rideLinks.some((item) => item.path === '/bookings') && (
            <MenuItem onClick={() => navigateTo('/bookings')} sx={menuItemSx}>
              {t('my_bookings') || 'My trips'}
            </MenuItem>
          )}
        </Menu>
      </ListItem>

      {/* Drive Menu */}
      <ListItem className={classes.listItem}>
        <Button
          aria-haspopup="true"
          aria-expanded={Boolean(driveAnchor) ? 'true' : 'false'}
          color="transparent"
          className={classes.navLink}
          onClick={handleDriveClick}
          endIcon={<KeyboardArrowDownIcon sx={{ ml: -0.5, fontSize: 20 }} />}
        >
          {t('drive') || 'Drive'}
        </Button>
        <Menu
          id="drive-menu"
          anchorEl={driveAnchor}
          open={Boolean(driveAnchor)}
          onClose={handleDriveClose}
          TransitionComponent={Grow}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={menuPaperProps}
        >
          {driveLinks.map((item, idx) => (
            <MenuItem key={`drive-${idx}`} onClick={() => goToLink(item)} sx={menuItemSx}>
              {t(item.label) || item.label}
            </MenuItem>
          ))}
        </Menu>
      </ListItem>

      {/* About Menu */}
      <ListItem className={classes.listItem}>
        <Button
          aria-haspopup="true"
          aria-expanded={Boolean(aboutAnchor) ? 'true' : 'false'}
          color="transparent"
          className={
            isActive('/about-us') || isActive('/contact-us') || isActive('/privacy-policy')
              ? classes.navLink + ' ' + classes.navLinkActive
              : classes.navLink
          }
          onClick={handleAboutClick}
          endIcon={<KeyboardArrowDownIcon sx={{ ml: -0.5, fontSize: 20 }} />}
        >
          {t('about') || 'About'}
        </Button>
        <Menu
          id="about-menu"
          anchorEl={aboutAnchor}
          open={Boolean(aboutAnchor)}
          onClose={handleAboutClose}
          TransitionComponent={Grow}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={menuPaperProps}
        >
          {aboutLinks.map((item, idx) => (
            <MenuItem key={`about-${idx}`} onClick={() => goToLink(item)} sx={menuItemSx}>
              {t(item.label) || item.label}
            </MenuItem>
          ))}
        </Menu>
      </ListItem>

      {/* Language Selector */}
      <ListItem className={classes.listItem}>
        <LanguageMenu />
      </ListItem>

      {/* Login/Signup or Account Button */}
      <ListItem className={classes.listItem}>
        {loggedIn ? (
          <Button
            color="transparent"
            className={classes.navLink}
            onClick={() => navigateTo('/bookings')}
            sx={{
              bgcolor: 'rgba(255,255,255,0.1)',
              borderRadius: '500px',
              px: 3,
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.2)',
              },
            }}
          >
            {t('myaccount') || 'Account'}
          </Button>
        ) : (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              color="transparent"
              className={classes.navLink}
              onClick={() => navigateTo('/login')}
              sx={{
                bgcolor: 'rgba(255,255,255,0.1)',
                borderRadius: '500px',
                px: 2.5,
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.2)',
                },
              }}
            >
              {t('login') || 'Log in'}
            </Button>
            <Button
              color="transparent"
              onClick={() => navigateTo('/login')}
              sx={{
                bgcolor: '#fff',
                color: '#000',
                borderRadius: '500px',
                px: 2.5,
                fontWeight: 600,
                '&:hover': {
                  bgcolor: '#f5f5f5',
                },
              }}
            >
              {t('signup') || 'Sign up'}
            </Button>
          </Box>
        )}
      </ListItem>
    </List>
  );
}
